import { audio } from '@/audio-singleton';
import { gameSignals } from '@/bridge/gameSignals';
import { t } from '@/i18n';
import { StatsBarsIcon } from './icons';
import type { LocalStateStore } from '@/lib/local-state';
import type { UiPrefs } from '@/lib/ui-prefs';
import { ENGAGEMENT_BADGES, unlockedEngagementBadges } from '@/lib/engagement-badges';

export interface PersonalStatsHeaderButtonProps {
  store: LocalStateStore;
  prefs: UiPrefs;
  onOpen: () => void;
}

export function PersonalStatsHeaderButton({ store, prefs, onOpen }: PersonalStatsHeaderButtonProps) {
  const scene = gameSignals.currentScene.value;
  if (scene !== 'MENU' && scene !== 'RESULT') return null;

  const longest = store.getStreak().longest;
  const seen = prefs.getPersonalStatsLastSeenLongest();
  const unlocked = unlockedEngagementBadges(longest);
  const dotActive = unlocked.length > unlockedEngagementBadges(seen).length;

  const label = dotActive ? t('personalStats.titleWithUnseen') : t('personalStats.title');

  const newestId = dotActive ? unlocked[unlocked.length - 1] : null;
  const newestMeta = newestId ? ENGAGEMENT_BADGES.find((b) => b.id === newestId) : null;
  const liveText = newestMeta
    ? t('personalStats.newBadgeAnnounce', { name: t(newestMeta.i18nKey as never) })
    : '';

  return (
    <>
      <button
        type="button"
        class="p-2 rounded-full text-text-2 hover:text-text hover:bg-text/5 active:bg-text/10 transition-colors relative"
        aria-label={label}
        onClick={() => { audio().play('tap'); onOpen(); }}
      >
        <StatsBarsIcon size={20} />
        {dotActive && (
          <span
            data-testid="personal-stats-dot"
            class="stats-dot-pulse absolute top-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-bg/85"
            aria-hidden="true"
          />
        )}
      </button>
      {dotActive && (
        <span
          data-testid="personal-stats-live"
          class="sr-only"
          aria-live="polite"
        >{liveText}</span>
      )}
    </>
  );
}
